import { createSlice } from "@reduxjs/toolkit";
import type { PayloadAction } from "@reduxjs/toolkit";
import moment from "moment";

import {
  INote,
  IProductHistory,
  TBuyBoxAnalysis,
  TEligibility,
  TOffer,
  TProductDetail,
} from "./productAnalyzer.types";
import { processCSVData, transformKeepaCsv } from "../../helpers/csv";
import { capitalize } from "../../helpers";
import {
  getProductDetails,
  getKeepaThunk,
  getNotesThunk,
  getAveragesThunk,
  getProductHistoriesThunk,
  createNoteThunk,
  deleteNoteThunk,
  updateNoteThunk,
  createProductTagThunk,
  deleteProductTagThunk,
  updateLookupThunk,
  getProductTagsThunk,
  getLookupThunk,
} from "./productAnalyzer.thunk";
import { initialState } from "./initialState";
import { IGetProductHistoriesResponse } from "../../services/apiV2.types";
import { ILookup } from "../lookups/lookups.types";
import { useAppDispatch } from "../../store/hooks";
export const productAnalyzerSlice = createSlice({
  name: "productAnalyzer",
  initialState,
  reducers: {
    addProductHistory: (state, action: PayloadAction<IProductHistory>) => {
      state.histories.unshift(action.payload);
      if (state.productDetail) {
        if (action.payload.type === "purchase") {
          state.productDetail.isPurchased = true;
        } else if (action.payload.type === "replenish") {
          state.productDetail.isReplenished = true;
        }
      }
    },
    updateProductDetail: (
      state,
      action: PayloadAction<Partial<TProductDetail>>,
    ) => {
      state.productDetail = {
        ...state.productDetail,
        ...action.payload,
      } as TProductDetail;
    },
    updateEligibility: (state, action: PayloadAction<TEligibility>) => {
      if (state.quickInfo) {
        state.quickInfo.eligibility = action.payload;
      }
    },
  },
  extraReducers: (builder) => {
    builder.addCase(getProductDetails.pending, (state) => {
      state.productDetail = null;
      state.quickInfo = null;
      state.restrictions = null;
      state.loadingRestrictions = true;
      state.eBay = initialState.eBay;
      state.sellerCentral = initialState.sellerCentral;
      state.profitCalculator = initialState.profitCalculator;
      state.alerts = initialState.alerts;
      state.chartsData = initialState.chartsData;
      state.averages = initialState.averages;
      state.offers = initialState.offers;
      state.loadingRanks = true;
      state.loadingProductDetail = true;
      state.loadingBuyBox = true;
      state.loadingAverages = true;
      state.loadingOffers = true;
      state.loadingChartsData = true;
    });
    builder.addCase(
      getProductDetails.fulfilled,
      (state, action: PayloadAction<any>) => {
        state.productDetail = {
          ...(action.payload?.productDetails ?? {}),
          lookupId: action.payload.lookupId,
        };
        state.quickInfo = {
          ...(action.payload?.quickInfo ?? {}),
          buyBoxCompetitor: action.payload?.buyBoxCompetitor,
        };
        state.restrictions = action.payload?.restrictions ?? null;
        state.loadingRestrictions = false;
        state.loadingProductDetail = false;

        state.profitCalculator = action.payload.profitCalculator;
        state.eBay = action.payload?.eBay;
        state.sellerCentral = action.payload?.sellerCentral;
        state.feeRules = {
          fbaFeeRule: action.payload?.feeRules?.fbaFee || [],
          referralFeeRule:
            action.payload?.feeRules?.referral?.referralFeeRule || "",
          inboundPlacementServiceFeeRule:
            action.payload?.feeRules?.inboundPlacement?.splits || [],
        };
        state.alerts = action.payload?.alerts;
        state.variations = action.payload?.variations?.productVariations || [];
        state.tags = action.payload?.tags || [];

        state.ranksAndPrices = action.payload?.ranks.ranks;
        state.loadingRanks = false;

        // state.buyBoxAnalysis = transformApiResponseToTableData(action.payload?.keepa, 30);
        state.buyBoxSellerIdHistory =
          action.payload?.buyBoxDataDto?.buyBoxSellerIdHistory || [];
        state.loadingBuyBox = false;

        state.loadingAverages = false;
        const lowestFBA: any = {};
        const lowestFBM: any = {};
        ["30", "90", "180", "Current", "All"].forEach((period) => {
          lowestFBA[period] = {
            average:
              action.payload?.averages?.lowestFBA?.[`avg${period}`] || null,
            minimum:
              action.payload?.minMax?.[`minMax${period}`]?.lowestFBA?.minimum ||
              null,
            maximum:
              action.payload?.minMax?.[`minMax${period}`]?.lowestFBA?.maximum ||
              null,
          };
          lowestFBM[period] = {
            average:
              action.payload?.averages?.lowestFBM?.[`avg${period}`] || null,
            minimum:
              action.payload?.minMax?.[`minMax${period}`]?.lowestFBM?.minimum ||
              null,
            maximum:
              action.payload?.minMax?.[`minMax${period}`]?.lowestFBM?.maximum ||
              null,
          };
        });
        if (!state.averages || Object.keys(state.averages).length === 0) {
          state.averages = {
            ...action.payload?.averages,
            lowestFBA,
            lowestFBM,
          };
        }

        state.loadingOffers = false;
        if (!state.offers || Object.keys(state.offers).length === 0) {
          const selleroffer = action.payload?.sellerOffers.selleroffer ?? null;
          if (Array.isArray(selleroffer)) {
            state.offers = selleroffer.map((o) => ({
              sellerId: o.sellerId,
              fulfillment: o.fulfillment,
              stock: o.stock,
              price: o.price,
              isPrime: o.isPrime,
            }));
          }
        }
        if (!state.chartsData || Object.keys(state.chartsData).length === 0) {
          const chartsData = action.payload?.chartData;
          state.csv = chartsData?.csv || [];
          const AmazonData = processCSVData(chartsData?.csv?.[0]);
          const FBAData = processCSVData(chartsData?.csv?.[10]);
          const FBMData = processCSVData(chartsData?.csv?.[7]);
          const buyBoxData = processCSVData(
            chartsData?.csv?.[18],
            null,
            "buyBoxData",
          );
          const newData = processCSVData(chartsData?.csv?.[1]);
          const salesRankData = processCSVData(
            chartsData?.csv?.[3],
            "notPrice",
          );
          const monthlySoldData = processCSVData(
            chartsData?.monthlySoldHistory,
            "notPrice",
          );
          const offersCountData = processCSVData(
            chartsData?.csv?.[11],
            "notPrice",
            "offersCountData",
          );

          const currentUnixTime = Math.floor(Date.now() / 1000);
          const date = new Date(currentUnixTime * 1000);
          const formattedDate = date.toISOString(); // <-- convert to ISO string

          const transformedData = transformKeepaCsv(
            chartsData?.csv?.[16],
            "RATING",
          );

          let ratingData = transformedData.map(([dateStr, price]) => ({
            date: new Date(dateStr).toISOString(), // <-- convert to string
            price,
          }));

          if (ratingData.length > 0) {
            let lastObject = ratingData[ratingData.length - 1];
            ratingData.push({
              date: formattedDate, // already ISO string
              price: lastObject.price,
            });
          }

          const reviewCountData = processCSVData(
            chartsData?.csv?.[17],
            "ReviewCount",
          );

          const monthlySold = {
            date: formattedDate, // ISO string
            price: chartsData?.monthlySold || 0,
          };

          if (monthlySoldData.length) monthlySoldData.push(monthlySold);

          const allProcessedData = [
            AmazonData,
            FBAData,
            FBMData,
            buyBoxData,
            newData,
            salesRankData,
            monthlySoldData,
            offersCountData,
            ratingData,
            reviewCountData,
          ];

          state.chartsData = JSON.parse(JSON.stringify(allProcessedData));
        }
        state.loadingChartsData = false;

        state.lastUpdate = action.payload?.lastUpdate;
      },
    );
    builder.addCase(
      getProductDetails.rejected,
      (state, action: PayloadAction<any>) => {
        state.productDetail = null;
        state.quickInfo = null;
        state.restrictions = null;
        state.loadingRestrictions = false;
        state.eBay = initialState.eBay;
        state.sellerCentral = initialState.sellerCentral;
        state.profitCalculator = initialState.profitCalculator;
        state.alerts = initialState.alerts;
        state.variations = initialState.variations;
        state.loadingBuyBox = false;
        state.loadingAverages = false;
        state.loadingOffers = false;
        state.loadingChartsData = false;
      },
    );

    // getKeepa
    builder.addCase(getKeepaThunk.pending, (state) => {
      state.loadingKeepa = true;
    });
    builder.addCase(
      getKeepaThunk.fulfilled,
      (state, action: PayloadAction<any>) => {
        state.loadingKeepa = false;
        state.keepa = action?.payload;
      },
    );
    builder.addCase(
      getKeepaThunk.rejected,
      (state, action: PayloadAction<any>) => {
        state.loadingKeepa = false;
        state.keepa = initialState.keepa;
      },
    );

    // Get notes
    builder.addCase(getNotesThunk.pending, (state) => {
      state.loadingNotes = true;
    });
    builder.addCase(
      getNotesThunk.fulfilled,
      (state, action: PayloadAction<INote[]>) => {
        state.loadingNotes = false;
        state.notes = action.payload;
      },
    );
    builder.addCase(getNotesThunk.rejected, (state) => {
      state.loadingNotes = false;
      state.notes = initialState.notes;
    });

    // Create note
    builder.addCase(
      createNoteThunk.fulfilled,
      (state, action: PayloadAction<INote>) => {
        state.notes.unshift(action.payload);
      },
    );

    // Delete note
    builder.addCase(
      deleteNoteThunk.fulfilled,
      (state, action: PayloadAction<string>) => {
        state.notes = state.notes.filter((note) => note.id !== action.payload);
      },
    );

    // Update note
    builder.addCase(
      updateNoteThunk.fulfilled,
      (state, action: PayloadAction<INote>) => {
        state.notes = state.notes.map((note) =>
          note.id === action.payload.id ? { ...note, ...action.payload } : note,
        );
      },
    );

    builder.addCase(getAveragesThunk.pending, (state) => {
      state.loadingAverages = true;
      state.averages = initialState.averages;
    });
    builder.addCase(
      getAveragesThunk.fulfilled,
      (state, action: PayloadAction<any>) => {
        state.loadingAverages = false;
        const lowestFBA: any = {};
        const lowestFBM: any = {};
        ["30", "90", "180", "Current", "All"].forEach((period) => {
          lowestFBA[period] = {
            average:
              action.payload?.averages?.lowestFBA?.[`avg${period}`] || null,
            minimum:
              action.payload?.minMax?.[`minMax${period}`]?.lowestFBA?.minimum ||
              null,
            maximum:
              action.payload?.minMax?.[`minMax${period}`]?.lowestFBA?.maximum ||
              null,
          };
          lowestFBM[period] = {
            average:
              action.payload?.averages?.lowestFBM?.[`avg${period}`] || null,
            minimum:
              action.payload?.minMax?.[`minMax${period}`]?.lowestFBM?.minimum ||
              null,
            maximum:
              action.payload?.minMax?.[`minMax${period}`]?.lowestFBM?.maximum ||
              null,
          };
        });
        state.averages = { ...action.payload?.averages, lowestFBA, lowestFBM };
        state.buyBoxSellerIdHistory =
          action.payload?.buyBoxDataDto?.buyBoxSellerIdHistory ||
          state.buyBoxSellerIdHistory ||
          [];
        const offers = action.payload?.sellerOffers?.selleroffer || [];
        if (offers.length > 0) {
          state.offers = offers.map((o: any) => ({
            sellerId: o.sellerId,
            fulfillment: o.fulfillment,
            stock: o.stock,
            price: o.price,
            isPrime: o.isPrime,
          }));
        }
        const chartsData = action.payload?.chartData;
        state.csv = chartsData?.csv || state.csv || [];
        const AmazonData = processCSVData(chartsData?.csv?.[0]);
        const FBAData = processCSVData(chartsData?.csv?.[10]);
        const FBMData = processCSVData(chartsData?.csv?.[7]);
        const buyBoxData = processCSVData(
          chartsData?.csv?.[18],
          null,
          "buyBoxData",
        );
        const newData = processCSVData(chartsData?.csv?.[1]);
        const salesRankData = processCSVData(chartsData?.csv?.[3], "notPrice");
        const monthlySoldData = processCSVData(
          chartsData?.monthlySoldHistory,
          "notPrice",
        );
        const offersCountData = processCSVData(
          chartsData?.csv?.[11],
          "notPrice",
          "offersCountData",
        );

        const currentUnixTime = Math.floor(Date.now() / 1000);
        const date = new Date(currentUnixTime * 1000);
        const formattedDate = date.toISOString(); // <-- convert to ISO string

        const transformedData = transformKeepaCsv(
          chartsData?.csv?.[16],
          "RATING",
        );

        let ratingData = transformedData.map(([dateStr, price]) => ({
          date: new Date(dateStr).toISOString(), // <-- convert to string
          price,
        }));

        if (ratingData.length > 0) {
          let lastObject = ratingData[ratingData.length - 1];
          ratingData.push({
            date: formattedDate, // already ISO string
            price: lastObject.price,
          });
        }

        const reviewCountData = processCSVData(
          chartsData?.csv?.[17],
          "ReviewCount",
        );

        const monthlySold = {
          date: formattedDate, // ISO string
          price: chartsData?.monthlySold || 0,
        };

        if (monthlySoldData.length) monthlySoldData.push(monthlySold);

        const allProcessedData = [
          AmazonData,
          FBAData,
          FBMData,
          buyBoxData,
          newData,
          salesRankData,
          monthlySoldData,
          offersCountData,
          ratingData,
          reviewCountData,
        ];
        state.chartsData = JSON.parse(JSON.stringify(allProcessedData));
        state.lastUpdate = action.payload?.lastUpdate;
      },
    );
    builder.addCase(getAveragesThunk.rejected, (state) => {
      state.loadingAverages = false;
      state.loadingOffers = false;
      state.loadingChartsData = false;
    });

    // Get histories
    builder.addCase(getProductHistoriesThunk.pending, (state, action) => {
      state.loadingHistories = true;
      if (action.meta.arg.page === 1) {
        state.histories = [];
        state.historyCount = 0;
      }
    });
    builder.addCase(
      getProductHistoriesThunk.fulfilled,
      (state, action: PayloadAction<IGetProductHistoriesResponse>) => {
        state.loadingHistories = false;
        action.payload.data.forEach((history) => {
          const found = state.histories.find((v) => v.id === history.id);
          if (!found) {
            state.histories.push(history);
          }
        });
        state.historyCount = action.payload.total;
      },
    );
    builder.addCase(getProductHistoriesThunk.rejected, (state) => {
      state.loadingHistories = false;
    });

    // Get product tags
    builder.addCase(getProductTagsThunk.fulfilled, (state, action) => {
      state.tags = [...action.payload.tags];
    });

    // Create product tag
    builder.addCase(createProductTagThunk.fulfilled, (state, action) => {
      state.tags = [...state.tags, action.meta.arg.tagId];
    });

    // Delete product tag
    builder.addCase(deleteProductTagThunk.fulfilled, (state, action) => {
      const index = state.tags.findIndex(
        (tag) => tag === action.meta.arg.tagId,
      );
      if (index >= 0) state.tags.splice(index, 1);
    });

    // Update lookup data
    builder.addCase(updateLookupThunk.pending, (state, action) => {
      state.profitCalculator = {
        ...state.profitCalculator,
        ...action.meta.arg,
      };
    });

    // Get lookup
    builder.addCase(
      getLookupThunk.fulfilled,
      (state, action: PayloadAction<ILookup>) => {
        if (action.payload.lookupId === state.productDetail?.lookupId) {
          const {
            salePrice,
            costPrice,
            fulfilmentType,
            fbmCost,
            storageTime,
            prepFee,
            inboundShipping,
            inboundPlacementService,
            miscFee,
            miscFeePercent,
            totalDiscount,
            multipackQuantity,
            quantity,
          } = action.payload;
          state.profitCalculator = {
            ...state.profitCalculator,
            salePrice,
            costPrice,
            fulfilmentType,
            fbmCost,
            storageTime,
            prepFee,
            inboundShipping,
            inboundPlacementService,
            miscFee,
            miscFeePercent,
            totalDiscount,
            multipackQuantity: multipackQuantity || 1,
            quantity: quantity || 1,
          };
        }
      },
    );
  },
});

export const { addProductHistory, updateProductDetail, updateEligibility } =
  productAnalyzerSlice.actions;

export default productAnalyzerSlice.reducer;
